import { Tenant, type Asset, type WorkOrderTemplate } from '#/graphql/types';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NuvoGrid } from '@nuvolo/nuux-core/components/NuvoGrid';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Item from './Item';
import AssetSelect from './AssetSelect';
import TemplatesList from './TemplatesList';
import RecordPreview, { RecordItem } from '#/common/components/RecordPreview';
import { executionIntervals } from '#/modules/wo-template/utils/constants';
import { Impact, impacts, priorities, Priority, types } from '#/config/mappings/mappings';
import { BLANK_TEXT } from '#/modules/wo-template/utils/constants';
import useWOTemplateForAsset from '#/modules/wo-template/hooks/useWOTemplateForAsset';
import TenantSelector from '#/common/components/TenantSelector';
import If from '#/common/components/If';
import useTenantQuery from '#/modules/admin/tenants/hooks/useTenantQuery';
import { NuvoTextField } from '@nuvolo/nuux-core/components/NuvoTextField';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import TMMButton from '#/common/components/TMMButton';
import { StyledNuvoBox } from '#/common/styles/template';
import { PageActions } from '#/common/components/SubPage';
import FAIcon from '#/common/components/FAIcon';
import { color } from '@nuvolo/nuux-theme/utils';

export interface TemplatePickerProps {
  assetId?: string;
}

const getTemplatePreviewFields = (
  template: WorkOrderTemplate | undefined,
): RecordItem<WorkOrderTemplate>[] => [
  {
    items: [
      {
        dataField: 'executionInterval',
        label: 'Execution Interval',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
        render: () => (
          <NuvoText type="body" bold>
            {executionIntervals.find((x) => x.key === template?.executionInterval)?.label || '-'}
          </NuvoText>
        ),
      },
      {
        dataField: 'title',
        label: 'Title',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
      },
      {
        dataField: 'description',
        label: 'Description',
        dataType: 'string',
        gridOptions: { xs: 12 },
      },
      {
        dataField: 'type',
        label: 'Type',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
        render: () => (
          <NuvoText type="body" bold>
            {types[template?.type as keyof typeof types]?.label || '-'}
          </NuvoText>
        ),
      },
      {
        dataField: 'priority',
        label: 'Priority',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
        render: () => (
          <NuvoText type="body" bold>
            {priorities[Number(template?.priority) as Priority]?.label || '-'}
          </NuvoText>
        ),
      },
      {
        dataField: 'impact',
        label: 'Impact',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
        render: () => (
          <NuvoText type="body" bold>
            {impacts[Number(template?.impact) as Impact]?.label || '-'}
          </NuvoText>
        ),
      },
      {
        dataField: 'notes',
        label: 'Notes',
        dataType: 'string',
        gridOptions: { xs: 12 },
      },
    ],
  },
];

const TemplatePicker: React.FC<TemplatePickerProps> = ({ assetId: initialAssetId = '' }) => {
  const { isDesktop } = useViewport();
  const [asset, setAsset] = useState<Asset | undefined>(undefined);
  const [template, setTemplate] = useState<WorkOrderTemplate>();

  const templateQuery = useWOTemplateForAsset(asset);
  const templateResult = templateQuery.data?.getWorkOrderTemplateList?.data || undefined;

  const [searchParams] = useSearchParams();
  const tenantIdFromParams = searchParams.get('tenantId') || undefined;

  const [selectedTenant, setTenant] = useState<Tenant>();
  const preselectedTenant = useTenantQuery(tenantIdFromParams, { skip: !tenantIdFromParams }).data
    ?.getTenant?.data;

  const tenant = preselectedTenant || selectedTenant;

  const templates = useMemo(() => {
    const tmpl = templateResult?.filter((t) => !!t) || [];

    return [
      {
        id: BLANK_TEXT,
        __typename: 'WorkOrderTemplate',
        title: 'Blank template',
        description: 'Create Work Order from scratch.',
      },
      ...tmpl,
    ];
  }, [templateResult]) as WorkOrderTemplate[];

  // Auto-select first template when templates change
  useEffect(() => {
    if (templates.length > 0 && !template) {
      setTemplate(templates[0]);
    }
  }, [templates, template]);

  const selectAsset = useCallback((newAsset: Asset | undefined) => {
    setAsset(newAsset);
    setTemplate(undefined);
  }, []);

  const navigate = useNavigate();
  const next = useCallback(() => {
    if (!tenant?.id || !asset?.id || !template?.id) {
      return;
    }

    const params = new URLSearchParams();
    params.set('assetId', asset?.id);
    params.set('templateId', template.id);
    params.set('tenantId', tenant?.id);
    const query = params.toString();

    navigate(`../new?${query}`, { replace: true });
  }, [tenant?.id, asset?.id, navigate, template?.id]);

  return (
    <StyledNuvoBox isDesktop={isDesktop}>
      <div className="section-label">
        <NuvoLabel>Work Order Details</NuvoLabel>
      </div>

      {!tenantIdFromParams && <TenantSelector onTenantSelect={setTenant} />}

      <NuvoBox display="flex" flexDirection="column" gap={20} id="tmm-section-no-background">
        <div className="organization-wrapper" style={{ width: isDesktop ? '50%' : '100%' }}>
          <NuvoLabel>Organization</NuvoLabel>
          <NuvoTextField value={tenant?.name || '[ Loading... ]'} readOnly />
        </div>
      </NuvoBox>

      <NuvoBox display="flex" flexDirection="column" gap={20} id="tmm-section">
        <div className="organization-wrapper">
          <NuvoLabel>Related Asset Name</NuvoLabel>
          <AssetSelect
            asset={asset}
            onSelect={selectAsset}
            tenantId={tenant?.id}
            initialAssetId={initialAssetId}
          />
        </div>
      </NuvoBox>

      <div className="section-label">
        <NuvoLabel>Select Work Order Template</NuvoLabel>
      </div>
      <NuvoBox display="flex" flexDirection="column" gap={20} id="tmm-section">
        <NuvoGrid container>
          <If condition={!!asset}>
            <NuvoGrid item xs={12} sm={6} className="tmm-section-panel">
              <NuvoBox
                display="flex"
                flexDirection="column"
                gap={12}
                backgroundColor={color('custom', 'dataGridBackground')}
                paddingTop={4}
                borderRadius="24px"
                height="100%">
                <If condition={!asset}>
                  <NuvoText type="header4">Please, select asset!</NuvoText>
                </If>
                <If condition={!!asset}>
                  <TemplatesList
                    loading={false}
                    asset={asset}
                    templates={templates}
                    selectedTemplate={template}
                    onSelect={setTemplate}
                  />
                </If>
              </NuvoBox>
            </NuvoGrid>

            <NuvoGrid item xs={12} sm={6} className="tmm-section-panel">
              <div style={{ marginBottom: '16px' }}>
                <Item>
                  <NuvoBox display="flex" alignItems="center" gap={8}>
                    <div
                      className="icon-wrapper"
                      style={{
                        padding: '8px',
                      }}>
                      <FAIcon color={color('custom', 'constantIconBlue')} icon="eye" size="xl" />
                    </div>
                    <span className="text-18">Preview</span>
                  </NuvoBox>
                  {template && template.id !== BLANK_TEXT ? (
                    <RecordPreview
                      record={template}
                      fields={getTemplatePreviewFields(template)}
                      noBorder
                      noPadding
                    />
                  ) : (
                    <NuvoBox display="flex" justifyContent="center" alignItems="center">
                      <div
                        style={{
                          width: '150px',
                          height: '274px',
                          textAlign: 'center',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}>
                        <NuvoBox display="flex" flexDirection="column" gap={16}>
                          <FAIcon icon="wrench" />
                          <NuvoText type="large">Create Work Order from Scratch</NuvoText>
                        </NuvoBox>
                      </div>
                    </NuvoBox>
                  )}
                </Item>
              </div>
              <TMMButton
                className="width-100"
                key="ok"
                label="Next"
                type="submit"
                disabled={!tenant || !asset || !template}
                onClick={next}
              />
            </NuvoGrid>
          </If>
        </NuvoGrid>
        <If condition={!asset}>
          <NuvoBox display="flex" justifyContent="center" alignItems="center">
            <div style={{ width: '200px', margin: '50px', textAlign: 'center' }}>
              <NuvoBox display="flex" flexDirection="column" gap={16}>
                <FAIcon icon="cube" />
                <NuvoText type="large">
                  Select an asset to see the available templates here
                </NuvoText>
              </NuvoBox>
            </div>
          </NuvoBox>
        </If>
      </NuvoBox>

      <PageActions
        actions={[
          {
            id: 'new-wo-next',
            type: 'button',
            alwaysShow: true,
            props: {
              type: 'submit',
              label: 'Next',
              disabled: !tenant || !asset || !template,
              onClick: next,
            },
          },
        ]}
      />
    </StyledNuvoBox>
  );
};

export default React.memo(TemplatePicker);
